import {useEffect, useMemo, useRef, useState} from "react";
import {Input} from "@heroui/input";
import {Button} from "@heroui/button";
import {Spinner} from "@heroui/spinner";
import {Search, X} from "lucide-react";

/**
 * GooglePlacesSearchDropdown (Combobox version)
 * --------------------------------------------------------------
 * A Google Places autocomplete that fetches both addresses
 * and business names (establishments). Suggestions are
 * rendered in a dropdown panel under the input.
 *
 * Env: VITE_GOOGLE_MAPS_API_KEY must be set.
 */
export default function GooglePlacesSearchDropdown({
  label = "Location",
  placeholder = "Search places...",
  /** initial text in the input (uncontrolled) */
  defaultValue = "",
  /** controlled selected value: a Google place_id */
  value = null,
  /** fired when the user selects a place from suggestions */
  onSelect = () => {},
  /** optional: fired when a controlled value (place_id) is resolved to details */
  onValueResolved = () => {},
  minLength = 2,
  debounceMs = 250,
  // Allow both addresses and businesses by default
  types = null,
  componentRestrictions = { country: ["za"] },
  className = ""
}) {
  const [ready, setReady] = useState(false);
  const [query, setQuery] = useState(defaultValue);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [predictions, setPredictions] = useState([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [resolvingValue, setResolvingValue] = useState(false);
  const [selectedPlaceId, setSelectedPlaceId] = useState(null);
  const [focused, setFocused] = useState(false); // NEW: only search when focused

  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const autoServiceRef = useRef(null);
  const detailsServiceRef = useRef(null);
  const sessionTokenRef = useRef(null);
  const debounceRef = useRef(null);

  // NEW: flag to ignore search effect when query changes programmatically
  const suppressSearchRef = useRef(false);

  const typesKey = useMemo(() => JSON.stringify(types), [types]);
  const compKey = useMemo(() => JSON.stringify(componentRestrictions ?? null), [componentRestrictions]);

  // ----- Load Google Maps Places JS -----
  useEffect(() => {
    let cancelled = false;

    const loadScript = () => {
      if (window.google?.maps?.places) return Promise.resolve();
      if (window.__gmapsPlacesLoading) return window.__gmapsPlacesLoading;

      const key = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
      if (!key) console.error("Missing VITE_GOOGLE_MAPS_API_KEY in your .env");

      window.__gmapsPlacesLoading = new Promise((resolve, reject) => {
        window.__onGMapsPlacesReady = () => resolve();
        const s = document.createElement("script");
        s.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=places&v=weekly&callback=__onGMapsPlacesReady`;
        s.async = true; s.defer = true;
        s.onerror = reject;
        document.head.appendChild(s);
      });
      return window.__gmapsPlacesLoading;
    };

    loadScript()
      .then(() => {
        if (cancelled) return;
        autoServiceRef.current = new window.google.maps.places.AutocompleteService();
        detailsServiceRef.current = new window.google.maps.places.PlacesService(document.createElement("div"));
        sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
        setReady(true);
      })
      .catch((err) => console.error("Failed to load Google Maps JS:", err));

    return () => {
      cancelled = true;
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  // ----- Close dropdown when clicking outside -----
  useEffect(() => {
    const onDown = (e) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  // ----- Fetch predictions for typed query -----
  useEffect(() => {
    if (!ready || !autoServiceRef.current) return;

    // Only search when the input is focused and the change was user-typed
    if (!focused || suppressSearchRef.current) return;

    if (debounceRef.current) clearTimeout(debounceRef.current);

    const short = (query?.trim()?.length || 0) < minLength;
    if (short) {
      setPredictions([]);
      setOpen(false);
      return;
    }

    setLoading(true);
    debounceRef.current = setTimeout(() => {
      const req = {
        input: query,
        sessionToken: sessionTokenRef.current,
        // if types is null, don’t restrict → return addresses + businesses
        types: types ? JSON.parse(typesKey) : undefined,
        componentRestrictions: JSON.parse(compKey)
      };
      autoServiceRef.current.getPlacePredictions(req, (preds, status) => {
        const OK = window.google.maps.places.PlacesServiceStatus.OK;
        const ZERO_RESULTS = window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS;
        if (status === OK && Array.isArray(preds)) {
          setPredictions(preds);
          setOpen(true);
        } else if (status === ZERO_RESULTS) {
          setPredictions([]);
          setOpen(true);
        } else {
          setPredictions([]);
          setOpen(false);
        }
        setActiveIndex(-1);
        setLoading(false);
      });
    }, debounceMs);
  }, [query, ready, minLength, debounceMs, typesKey, compKey, focused]);

  // ----- Helper: resolve a place_id to display text + details -----
  const resolvePlaceId = (placeId) =>
    new Promise((resolve) => {
      if (!placeId || !detailsServiceRef.current) return resolve(null);
      const req = {
        placeId,
        fields: [
          "place_id",
          "name",
          "formatted_address",
          "address_components",
          "geometry",
          "types"
        ],
      };
      detailsServiceRef.current.getDetails(req, (place, status) => {
        const OK = window.google.maps.places.PlacesServiceStatus.OK;
        if (status !== OK || !place) return resolve(null);
        const lat = place.geometry?.location?.lat?.();
        const lng = place.geometry?.location?.lng?.();
        resolve({
          place_id: place.place_id,
          name: place.name || "",
          formatted_address: place.formatted_address || "",
          address_components: place.address_components || [],
          types: place.types || [],
          lat,
          lng,
        });
      });
    });

  // ----- Sync controlled `value` (place_id) into the visible input -----
  useEffect(() => {
    let ignore = false;

    const doResolve = async () => {
      if (!ready) return; // wait until Places is ready
      if (!value) {
        // cleared externally
        setSelectedPlaceId(null);
        return;
      }
      if (value === selectedPlaceId) return;

      setResolvingValue(true);
      const details = await resolvePlaceId(value);
      setResolvingValue(false);
      if (ignore) return;

      if (details) {
        setSelectedPlaceId(details.place_id);

        // prefer "Name, Address" as display text
        const display =
          details.name && details.formatted_address
            ? `${details.name}, ${details.formatted_address}`
            : details.name || details.formatted_address || "";

        // Programmatic text update — suppress search & close panel
        suppressSearchRef.current = true;
        setQuery(display);
        setOpen(false);
        setPredictions([]);
        // Allow the effect to run again for user typing
        // Use a microtask so any synchronous effects won't catch this as true
        Promise.resolve().then(() => { suppressSearchRef.current = false; });

        try { onValueResolved(details); } catch {}
      }
    };

    doResolve();
    return () => { ignore = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, ready]);

  // ----- Selecting a prediction from the dropdown -----
const selectPrediction = (pred) => {
  if (!pred || !detailsServiceRef.current) return;

  const req = {
    placeId: pred.place_id,
    sessionToken: sessionTokenRef.current,
    fields: [
      "place_id",
      "name",
      "formatted_address",
      "address_components",
      "geometry",
      "types"
    ]
  };

  detailsServiceRef.current.getDetails(req, (place, status) => {
    const OK = window.google.maps.places.PlacesServiceStatus.OK;
    if (status !== OK || !place) return;

    const lat = place.geometry?.location?.lat?.();
    const lng = place.geometry?.location?.lng?.();

    const payload = {
      place_id: place.place_id,
      description: pred.description,
      name: place.name,
      formatted_address: place.formatted_address,
      lat, lng,
      address_components: place.address_components,
      types: place.types
    };

    const display =
      payload.name && payload.formatted_address
        ? `${payload.name}, ${payload.formatted_address}`
        : payload.description || payload.name || payload.formatted_address || "";

    // Programmatic update → suppress fetch + close + defocus
    suppressSearchRef.current = true;
    setQuery(display);
    setSelectedPlaceId(payload.place_id);
    setPredictions([]);
    setOpen(false);
    setFocused(false);                        // 👈 stop search effect
    if (inputRef.current) inputRef.current.blur(); // 👈 remove focus

    // allow user typing later to re-enable searching
    setTimeout(() => { suppressSearchRef.current = false; }, 0);

    onSelect(payload);

    sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
  });
};

  // ----- Keyboard navigation -----
  const onKeyDown = (e) => {
    if (!open || predictions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((idx) => Math.min(idx + 1, predictions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((idx) => Math.max(idx - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const choice = predictions[activeIndex] || predictions[0];
      if (choice) selectPrediction(choice);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const clear = () => {
    suppressSearchRef.current = true; // avoid immediate fetch on clearing programmatically
    setQuery("");
    setPredictions([]);
    setOpen(false);
    setActiveIndex(-1);
    Promise.resolve().then(() => { suppressSearchRef.current = false; });
    // parent still owns `value` (controlled), so we don't change it here
  };

  return (
    <div ref={wrapperRef} className={`relative w-full ${className}`}>
      <Input
        ref={inputRef}
        placeholder={placeholder}
        value={query}
        onFocus={() => { setFocused(true); }}
        onBlur={() => { setFocused(false); setTimeout(() => setOpen(false), 100); }}
        onValueChange={(v) => {
          setQuery(v);
          if ((v?.trim()?.length || 0) < minLength) {
            setOpen(false);
            setPredictions([]);
          }
        }}
        onKeyDown={onKeyDown}
        endContent={
          <div className="flex items-center gap-2">
            {(loading || resolvingValue) && <Spinner size="sm" />}
            {query && (
              <Button isIconOnly variant="light" size="sm" onPress={clear}>
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        }
        classNames={{
          inputWrapper: `
                  bg-white border border-neutral-200 hover:bg-neutral-50 rounded-xl shadow-sm
                  dark:bg-content1/70 dark:border-default-200/60 dark:hover:bg-content2/60 dark:shadow-none
                  focus:outline-none focus:ring-0
                  data-[focus=true]:ring-0 data-[focus=true]:outline-none data-[focus=true]:border-neutral-300
                `,
                input: `
                  text-neutral-900 placeholder:text-neutral-400 text-base
                  dark:text-foreground focus:outline-none focus:ring-0
                `
        }}
      />

      {open && (
        <div
          role="listbox"
          aria-label="Google places suggestions"
          className="
            absolute z-50 mt-1 max-h-80 w-full overflow-auto rounded-xl
            border border-neutral-200 bg-white shadow-xl
            dark:border-default-200 dark:bg-content1
          "
        >
          {predictions.length === 0 ? (
            <div className="p-3 text-sm text-neutral-600 dark:text-default-500">No results</div>
          ) : (
            predictions.map((p, idx) => (
              <div
                key={p.place_id + idx}
                role="option"
                aria-selected={activeIndex === idx}
                onMouseEnter={() => setActiveIndex(idx)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => selectPrediction(p)}
                className={`
                  cursor-pointer px-3 py-2 text-sm
                  hover:bg-neutral-100 dark:hover:bg-default-100
                  ${activeIndex === idx ? "bg-neutral-100 dark:bg-default-100" : ""}
                `}
              >
                <div className="flex flex-col">
                  <span className="text-neutral-900 dark:text-foreground font-medium">
                    {p.structured_formatting?.main_text || p.description}
                  </span>
                  <span className="text-xs text-neutral-600 dark:text-default-500">
                    {p.structured_formatting?.secondary_text || ""}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}